import { useCallback, useRef, useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Space, Select, Input, Tag, Modal, message } from 'antd';
import { PlusOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { DataGrid, type DataGridHandle } from '@/components/DataGrid';
import type { SearchParams } from '@/api/types';
import { listCustomers, deleteCustomer, type Customer, type CustomerFilters } from '@/api/customerApi';
import { useAuth } from '@/hooks/useAuth';

const STATUS_COLORS: Record<string, string> = {
  active: 'green',
  inactive: 'default',
  prospect: 'blue',
  lead: 'orange',
  churned: 'red',
};

export default function CustomerListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const gridRef = useRef<DataGridHandle>(null);

  const [filters, setFilters] = useState<CustomerFilters>({});

  const canWrite = hasPermission('customers', 'write');
  const canDelete = hasPermission('customers', 'delete');

  const handleFilterChange = useCallback(
    (key: keyof CustomerFilters, value: string | undefined) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  const fetchData = useCallback(
    (params: SearchParams) => listCustomers(params, filters),
    [filters]
  );

  // Reload grid when filters change (skip initial mount)
  const filterMounted = useRef(false);
  useEffect(() => {
    if (!filterMounted.current) {
      filterMounted.current = true;
      return;
    }
    gridRef.current?.reload();
  }, [filters]);

  const handleDelete = useCallback(
    (id: string) => {
      Modal.confirm({
        title: t('app.confirm'),
        content: t('customers.confirm_delete'),
        okText: t('app.yes'),
        cancelText: t('app.no'),
        okType: 'danger',
        onOk: async () => {
          try {
            await deleteCustomer(id);
            message.success(t('customers.delete_success'));
            gridRef.current?.reload();
          } catch {
            message.error(t('app.error'));
          }
        },
      });
    },
    [t]
  );

  const columns: ColumnsType<Customer> = useMemo(
    () => [
      {
        title: t('customers.field.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true,
        render: (name: string, record: Customer) => (
          <a onClick={() => navigate(`/customers/${record.id}`)}>{name}</a>
        ),
      },
      {
        title: t('customers.field.company'),
        dataIndex: 'company',
        key: 'company',
        sorter: true,
        ellipsis: true,
      },
      {
        title: t('customers.field.email'),
        dataIndex: 'email',
        key: 'email',
        ellipsis: true,
      },
      {
        title: t('customers.field.phone'),
        dataIndex: 'phone',
        key: 'phone',
      },
      {
        title: t('customers.field.status'),
        dataIndex: 'status',
        key: 'status',
        width: 100,
        render: (status: string) => (
          <Tag color={STATUS_COLORS[status] ?? 'default'}>
            {t(`customers.status_options.${status}`, status)}
          </Tag>
        ),
      },
      {
        title: t('customers.field.industry'),
        dataIndex: 'industry',
        key: 'industry',
        ellipsis: true,
      },
      {
        title: t('customers.field.source'),
        dataIndex: 'source',
        key: 'source',
      },
      {
        title: t('customers.field.assigned_to'),
        dataIndex: 'assigned_to',
        key: 'assigned_to',
        ellipsis: true,
      },
      {
        title: t('customers.field.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        width: 170,
        render: (val: string) => val ? new Date(val).toLocaleString() : '-',
      },
      {
        title: t('app.actions'),
        key: 'actions',
        width: 150,
        render: (_: unknown, record: Customer) => (
          <Space size="small">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => navigate(`/customers/${record.id}`)}
            />
            {canWrite && (
              <Button
                type="link"
                size="small"
                icon={<EditOutlined />}
                onClick={() => navigate(`/customers/${record.id}/edit`)}
              />
            )}
            {canDelete && (
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record.id)}
              />
            )}
          </Space>
        ),
      },
    ],
    [t, navigate, canWrite, canDelete, handleDelete]
  );

  const statusOptions = useMemo(
    () => [
      { value: '', label: t('customers.filter.all') },
      { value: 'active', label: t('customers.status_options.active') },
      { value: 'inactive', label: t('customers.status_options.inactive') },
      { value: 'prospect', label: t('customers.status_options.prospect') },
      { value: 'lead', label: t('customers.status_options.lead') },
      { value: 'churned', label: t('customers.status_options.churned') },
    ],
    [t]
  );

  const filterBar = (
    <Space wrap>
      <Select
        placeholder={t('customers.filter.status')}
        style={{ width: 140 }}
        allowClear
        options={statusOptions}
        onChange={(val) => handleFilterChange('status', val || undefined)}
      />
      <Input.Search
        placeholder={t('customers.filter.industry')}
        style={{ width: 160 }}
        allowClear
        onSearch={(val) => handleFilterChange('industry', val || undefined)}
        enterButton={false}
      />
      <Input.Search
        placeholder={t('customers.filter.source')}
        style={{ width: 160 }}
        allowClear
        onSearch={(val) => handleFilterChange('source', val || undefined)}
        enterButton={false}
      />
      <Input.Search
        placeholder={t('customers.filter.assigned_to')}
        style={{ width: 160 }}
        allowClear
        onSearch={(val) => handleFilterChange('assigned_to', val || undefined)}
        enterButton={false}
      />
    </Space>
  );

  const toolbar = (
    <Space>
      {filterBar}
      {canWrite && (
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate('/customers/new')}
        >
          {t('customers.new_customer')}
        </Button>
      )}
    </Space>
  );

  return (
    <div>
      <h2>{t('customers.list_title')}</h2>
      <DataGrid<Customer>
        ref={gridRef}
        columns={columns}
        fetchData={fetchData}
        rowKey="id"
        searchable
        searchPlaceholder={t('customers.search_placeholder')}
        defaultSortField="created_at"
        defaultSortDescending
        toolbar={toolbar}
        onRow={(record) => ({
          onDoubleClick: () => navigate(`/customers/${record.id}`),
          style: { cursor: 'pointer' },
        })}
      />
    </div>
  );
}
